/**
 * StepChrome (BLU-1376, layout B): the per-step frame data the page supplies
 * to whichever step body is mounted.
 *
 * The page knows the step's number, group, state, what unlocks it and the
 * footer actions; the step body only knows its own content. The page wraps the
 * ONE mounted body in a provider and the body renders `StepPane`, which reads
 * the frame from here. That keeps step bodies free of wizard plumbing.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { StepId } from '../../lib/steps';
import type { StepVisualState } from '../../lib/step-state';

export interface StepChromeValue {
  stepId: StepId;
  /** 1-based position in the visible walk. */
  number: number;
  /** Required steps only (matches the band meter and the Back to setup bar). */
  total: number;
  /** An optional step (never counted toward finishing): the eyebrow says Optional instead of Step N of M. */
  optional?: boolean;
  /** Already translated group name for the eyebrow. */
  groupLabel: string;
  state: StepVisualState;
  /** Set when the step is locked: the step that has to be finished first. */
  lockedTarget?: { label: string; onGo: () => void };
  /** Manual "Mark as done" override. Omit when the caller cannot write. */
  markDone?: {
    manuallyDone: boolean;
    complete: boolean;
    pending?: boolean;
    disabled?: boolean;
    onDone: () => void;
    onUndo: () => void;
  };
  /** "Skip" for an optional step. Omit when the step cannot be skipped. */
  skip?: {
    skipped: boolean;
    pending?: boolean;
    disabled?: boolean;
    onSkip: () => void;
    onUnskip: () => void;
  };
  /** The next step, offered in the footer once this step is complete. */
  next?: { label: string; onGo: () => void };
  /** One-shot unlock glow on the card (the page clears it after the keyframe). */
  glow: boolean;
  /** The page focuses this heading when the step becomes the selected one. */
  headingRef: (el: HTMLHeadingElement | null) => void;
}

const StepChromeContext = createContext<StepChromeValue | null>(null);

export function StepChromeProvider({ value, children }: { value: StepChromeValue; children: ReactNode }) {
  return <StepChromeContext.Provider value={value}>{children}</StepChromeContext.Provider>;
}

export function useStepChrome(): StepChromeValue {
  const value = useContext(StepChromeContext);
  if (!value) {
    throw new Error('useStepChrome must be used inside a StepChromeProvider');
  }
  return value;
}
